import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// The active org → workspace → app → environment. Everything the dashboard reads is scoped by it,
// so it is persisted: landing back on an empty picker after a refresh would be its own small bug.
//
// Environment is load-bearing, not a preference — it is the publish target and the scope of version
// history. That is why it sits in the top bar as its own control rather than buried in a menu.
interface ContextState {
  orgId: string | null;
  workspaceId: string | null;
  appId: string | null;
  environmentId: string | null;

  setOrg: (orgId: string) => void;
  setWorkspace: (workspaceId: string) => void;
  setApp: (appId: string) => void;
  setEnvironment: (environmentId: string) => void;
  reset: () => void;
}

const EMPTY = { orgId: null, workspaceId: null, appId: null, environmentId: null };

export const useContextStore = create<ContextState>()(
  persist(
    (set) => ({
      ...EMPTY,

      // Selecting higher up the tree invalidates everything below it — a workspace from org A with
      // an app from org B is not a state the API would ever accept, so it must not be a state the
      // UI can hold. The shell re-picks sensible defaults from the context tree.
      setOrg: (orgId) => set({ orgId, workspaceId: null, appId: null, environmentId: null }),
      setWorkspace: (workspaceId) => set({ workspaceId, appId: null, environmentId: null }),
      setApp: (appId) => set({ appId, environmentId: null }),
      setEnvironment: (environmentId) => set({ environmentId }),
      reset: () => set(EMPTY),
    }),
    { name: 'phq-context' }
  )
);
